import { authenticatedFetch, authenticatedGet, authenticatedPost, authenticatedPut } from "../../../api/authenticatedFetch";

const BASE = "/api/v1";

export async function getActiveModelVersion(dimensionId) {
  const data = await authenticatedGet(`${BASE}/cost-model-versions/active`, { dimension_id: dimensionId });
  return data;
}

export async function getCustomerMe() {
  const data = await authenticatedGet(`${BASE}/customer/me`);
  return data.customer ?? null;
}

export async function getBudgetDimensions() {
  const data = await authenticatedGet(`${BASE}/budget-dimensions`);
  return data.budget_dimensions || [];
}

export async function getCostModelVersions(dimensionId) {
  const data = await authenticatedGet(`${BASE}/cost-model-versions`, { dimension_id: dimensionId });
  return data.cost_model_versions || [];
}

export async function createCostModelVersion(body) {
  const data = await authenticatedPost(`${BASE}/cost-model-versions`, body);
  return data.cost_model_version;
}

export async function applyCostModelVersion(versionId, body = {}) {
  const data = await authenticatedPost(`${BASE}/cost-model-versions/${versionId}/apply`, body);
  return data.cost_model_version;
}

export async function archiveCostModelVersion(versionId) {
  const res = await authenticatedFetch(`${BASE}/cost-model-versions/${versionId}/archive`, { method: "POST", body: {} });
  if (res.status === 204) return;
  return res.json();
}

export async function getCostModelNodes(modelVersionId) {
  const data = await authenticatedGet(`${BASE}/cost-models/${modelVersionId}/nodes`);
  return data.nodes || [];
}

export async function createCostModelNode(modelVersionId, body) {
  const data = await authenticatedPost(`${BASE}/cost-models/${modelVersionId}/nodes`, body);
  return data.node;
}

export async function getCostModelNode(modelVersionId, nodeId) {
  const data = await authenticatedGet(`${BASE}/cost-models/${modelVersionId}/nodes/${nodeId}`);
  return data.node;
}

export async function updateCostModelNode(modelVersionId, nodeId, body) {
  const data = await authenticatedPut(`${BASE}/cost-models/${modelVersionId}/nodes/${nodeId}`, body);
  return data.node;
}

export async function deleteCostModelNode(modelVersionId, nodeId) {
  const res = await authenticatedFetch(`${BASE}/cost-models/${modelVersionId}/nodes/${nodeId}`, { method: "DELETE" });
  if (res.status === 204) return;
  return res.json();
}

// Audit/change history for an object. objectType e.g. "cost_model_node" | "cost_model_version".
// Returns an array of events: { id, event_type, actor_user_id, before, after, payload, event_time }.
export async function getAuditEvents(objectType, objectId, limit = 50) {
  const data = await authenticatedGet(`${BASE}/audit-events`, { object_type: objectType, object_id: objectId, limit });
  return data?.events || [];
}

// Kick off an allocation backfill (async). Body is optional { from, to } (YYYY-MM-DD);
// defaults to the trailing 12 months. Returns { logId, from, to, message } (HTTP 202).
export async function runAllocationBackfill(body = {}) {
  return authenticatedPost(`${BASE}/financial-ownership/backfill`, body);
}

// Per-node allocated cost (direct) + subtree rollup, trailing 12 months. Returns
// { period, currency, nodes: [{ nodeId, direct, subtree }] }. Meaningful for the active
// (applied) version only. Pass { proforma: true } to fetch simulated (not-yet-booked) figures.
export async function getCostModelNodeCosts(modelVersionId, { proforma = false } = {}) {
  const data = await authenticatedGet(`${BASE}/cost-models/${modelVersionId}/node-costs`, proforma ? { proforma: true } : {});
  return data || { nodes: [] };
}

export async function getCostModelEdges(modelVersionId) {
  const data = await authenticatedGet(`${BASE}/cost-models/${modelVersionId}/edges`);
  return data.edges || [];
}

export async function putCostModelEdges(modelVersionId, body) {
  const data = await authenticatedPut(`${BASE}/cost-models/${modelVersionId}/edges`, body);
  return data.edges || [];
}

export async function getPeriodLock() {
  const data = await authenticatedGet(`${BASE}/period-lock`);
  return data?.lockedThrough || null;
}

export async function setPeriodLock(lockedThrough) {
  return authenticatedPut(`${BASE}/period-lock`, { lockedThrough });
}
